import { useFetchCatalogoVisitasTraslados } from "@/shared/actions/erp/administration/catalogo-visitas-traslados.actions";

/** Slug del Parámetro (key-value Django) que guarda el árbol de tipos de instalación. */
export const SLUG_TIPOS_INSTALACION_VISITA = "tipos_instalacion_visita";

/** Subtipo de un tipo de instalación (p.ej. Empaquetado → Tipo 1..3 por rango). */
export type SubtipoInstalacion = {
  nombre: string;
  rango?: string;
};

/** Tipo de instalación (con o sin subtipos). */
export type TipoInstalacion = {
  nombre: string;
  es_default?: boolean;
  subtipos?: SubtipoInstalacion[];
};

/** Selección guardada en la visita: tipo + (opcional) subtipo + rango snapshoteado. */
export type TipoInstalacionSeleccion = {
  tipo: string;
  subtipo?: string;
  rango?: string;
  // Evidencia fotográfica del tipo de instalación ejecutada (sube el técnico).
  foto?: { url: string; fecha?: string } | null;
};

/** Árbol por defecto de tipos de instalación (semilla + fallback). */
export const TIPOS_INSTALACION_DEFAULT: TipoInstalacion[] = [
  { nombre: "Normal", es_default: true },
  { nombre: "Soterrada" },
  { nombre: "Ductería" },
  {
    nombre: "Empaquetado",
    subtipos: [
      { nombre: "Tipo 1", rango: "50m a 150m" },
      { nombre: "Tipo 2", rango: "151m a 300m" },
      { nombre: "Tipo 3", rango: "301m a 450m" },
    ],
  },
  { nombre: "Adosado" },
];

/** Normaliza un array crudo de tipos de instalación (asegura la forma). */
export function normalizarTiposInstalacion(raw: any): TipoInstalacion[] {
  if (!Array.isArray(raw) || raw.length === 0) return TIPOS_INSTALACION_DEFAULT;
  return raw
    .filter((t) => t && typeof t === "object" && t.nombre)
    .map((t) => ({
      nombre: String(t.nombre),
      es_default: !!t.es_default,
      subtipos: Array.isArray(t.subtipos)
        ? t.subtipos
            .filter((s: any) => s && s.nombre)
            .map((s: any) => ({ nombre: String(s.nombre), rango: s.rango || undefined }))
        : undefined,
    }));
}

/** Tipo por defecto (el marcado es_default, o el primero). */
export function tipoInstalacionPorDefecto(tipos: TipoInstalacion[]): TipoInstalacionSeleccion {
  const def = tipos.find((t) => t.es_default) || tipos[0];
  return def ? { tipo: def.nombre } : { tipo: "Normal" };
}

/**
 * Anexa una entrada al historial de trazabilidad SI el tipo de instalación cambió.
 * Devuelve el historial resultante (mismo array si no hubo cambio).
 */
export function trazaTipoInstalacion(
  previo: TipoInstalacionSeleccion | null | undefined,
  nuevo: TipoInstalacionSeleccion | null | undefined,
  historialPrevio: any[] | undefined,
  usuario: string,
  origen: string
): any[] {
  const hist = Array.isArray(historialPrevio) ? historialPrevio : [];
  const cambio = JSON.stringify(previo || null) !== JSON.stringify(nuevo || null);
  if (!cambio) return hist;
  return [
    ...hist,
    {
      usuario: usuario || "—",
      fecha: new Date().toISOString(),
      origen,
      anterior: previo || null,
      nuevo: nuevo || null,
    },
  ];
}

/** Etiqueta legible de una selección (p.ej. "Empaquetado — Tipo 2 (151m a 300m)"). */
export function etiquetaTipoInstalacion(sel?: TipoInstalacionSeleccion | null): string {
  if (!sel || !sel.tipo) return "—";
  if (sel.subtipo) {
    return `${sel.tipo} — ${sel.subtipo}${sel.rango ? ` (${sel.rango})` : ""}`;
  }
  return sel.tipo;
}

/**
 * Lee el árbol de tipos de instalación (JSON guardado en un Parametro por slug).
 * Estructura: { tipos: [ {nombre, es_default, subtipos:[{nombre,rango}]} ] }.
 * Devuelve el árbol (con fallback a los defaults), el registro Parametro (para
 * editarlo) y el estado de carga.
 */
export function useTiposInstalacion() {
  // El árbol ya no es un JSON dentro de Parametro: vive en la tabla
  // CatalogoTipoInstalacion y el endpoint lo entrega con la misma forma
  // ({ tipos: [...] }), así que la normalización de abajo no cambió.
  const { data, isLoading, isRefetching } = useFetchCatalogoVisitasTraslados();

  // Compat: ya no existe el registro Parametro; el panel guarda por el
  // endpoint nuevo (guardarCatalogoVisitasTraslados).
  const parametro: any = null;

  const parsed: any = (data as any)?.tipos_instalacion ?? null;

  const rawTipos = Array.isArray(parsed?.tipos)
    ? parsed.tipos
    : Array.isArray(parsed)
    ? parsed
    : null;
  const tipos = normalizarTiposInstalacion(rawTipos);

  return { tipos, parametro, isLoading, isRefetching };
}
